import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export type DropdownOption<T extends string> = { value: T; label: ReactNode }

/**
 * A select with our own list: the chosen option shows a check, the list
 * can carry logos, and it closes on a click elsewhere or Escape (the same
 * pattern as the notification bell and the account menu).
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
  disabled = false,
}: {
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  /** what the control is, for screen readers */
  label: string
  className?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const current = options.find((o) => o.value === value) ?? options[0]
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        type="button"
        className="control flex h-10 w-full items-center gap-2 px-3 text-left text-sm hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2 truncate">{current?.label}</span>
        <ChevronDown className={`size-4 shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        // as wide as its longest row, never narrower than the button (John, Nov 5 2026: "All tokens" was cut off by the check
        // in a list the width of the button); the rows are the place menu's
        <ul role="listbox" aria-label={label} className="card absolute left-0 top-full z-50 mt-2 grid max-h-72 w-max min-w-full max-w-[min(20rem,calc(100vw-2rem))] gap-0.5 overflow-auto p-1.5 shadow-lg shadow-black/40">
          {options.map((option) => {
            const selected = option.value === value
            return (
              <li key={option.value} role="option" aria-selected={selected}>
                <button
                  type="button"
                  className="btn btn-ghost w-full !justify-start gap-2.5 whitespace-nowrap !px-2 !py-2 text-sm"
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">{option.label}</span>
                  {selected ? <Check className="size-4 shrink-0 text-accent" /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
